import { AxiosInstance } from 'axios';

import { makeConsoleApiClient, ConsoleApiClient } from './console-api-client';
import { attachCookieSizeWatchdog } from './cookie-size-watchdog';
import { MiddlewareController } from './middleware/types';

type ApiClientConfig = {
  onUnauthorized: () => void;
  captureWarning?: (message: string, extra: Record<string, unknown>) => void;
};

type ApiClientInstance = {
  apiClient: ConsoleApiClient;
  middlewareController: MiddlewareController<ConsoleApiClient>;
};

export const createApiClientInstance = (
  client: AxiosInstance,
  config: ApiClientConfig
): ApiClientInstance => {
  const { apiClient, middlewareController } = makeConsoleApiClient(client);

  attachCookieSizeWatchdog(client, {
    captureWarning: config.captureWarning,
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const url = error?.config?.url;

      if (typeof url === 'string' && url.includes('agent.yaml')) {
        return Promise.reject(error);
      }

      if (
        url === 'auth/session' ||
        (typeof url === 'string' && url.includes('/chat/completions'))
      ) {
        return Promise.reject(error);
      }

      if (error?.response?.status === 401) {
        if (typeof url === 'string' && url.includes('/v1/me')) {
          config.onUnauthorized();
          return Promise.resolve();
        } else {
          return apiClient.user.currentUserProfile().then(
            () => Promise.reject(error),
            (profileError) => {
              if (profileError?.response?.status === 401) {
                config.onUnauthorized();
                return Promise.resolve();
              }
              return Promise.reject(error);
            }
          );
        }
      }

      return Promise.reject(error);
    }
  );

  return { apiClient, middlewareController };
};
